import React from 'react';
import LegalLayout from '../components/LegalLayout';
import { usePageMeta } from '../hooks/usePageMeta';

const SECTIONS = [
  {
    heading: '1. What we collect',
    bullets: [
      'Account details: your name and email address. Your password is stored only as a secure one-way hash, never as readable text.',
      'If you sign in with Google: your name, email address, Google account ID and profile picture. We never see your Google password.',
      'What you create in your personal space: your assessment answers and wellbeing check-ins, saved guides, guides you’ve read, habits, habit logs, challenges, weekly priorities, reflections and goals.',
      'Optional health information you choose to enter, such as health metrics or medication records. We ask for your separate, explicit consent before you store this.',
      'Usage information (only if you allow analytics): pages visited, clicks, device and browser type, and approximate location from your IP address.',
      'Messages you send us through the contact form or by email.',
    ],
    after: 'We do not knowingly collect information from anyone under 18. Personal accounts are for adults only.',
  },
  {
    heading: '2. Why we use it',
    bullets: [
      'To create and run your account and personal space.',
      'To remember what you save and show your progress.',
      'To reply to your messages and requests.',
      'With your consent, to understand how the site is used so we can improve it.',
      'To keep the site secure and meet legal requirements.',
    ],
    after: 'We do not sell your personal data, share it with advertisers, or use it for advertising.',
  },
  {
    heading: '3. Health information',
    paragraphs: [
      'Health metrics and medication records are sensitive. They are visible only to you, are never shown on public pages, and are never used for analytics or advertising. You can edit or delete them at any time.',
    ],
  },
  {
    heading: '4. Cookies and analytics',
    bullets: [
      'Essential cookies: secure session cookies that keep you signed in. These are needed for the site to work.',
      'Analytics (optional): with your consent, we use PostHog analytics, provided through our hosting platform Emergent, to understand which pages are useful. This may include anonymised session recordings of public pages. We never record your personal space or login pages, and we mask anything you type. Analytics only start if you click "Accept analytics", and you can change your choice at any time from the "Cookie settings" link in the footer.',
    ],
  },
  {
    heading: '5. Who we share data with',
    paragraphs: ['We share data only with service providers who help us run the site, under contracts that protect it:'],
    bullets: [
      'Emergent: website hosting and infrastructure.',
      'Google: only if you choose "Continue with Google".',
      'PostHog: analytics, only if you consent.',
      'Resend: to send account emails (such as password resets) and replies to messages you send us.',
    ],
    after: 'We may also disclose data if the law requires it. Some providers may store data on servers outside India, with appropriate safeguards.',
  },
  {
    heading: '6. How long we keep it',
    bullets: [
      'Account and personal-space data: until you delete it or close your account. We then delete it within 30 days. Copies in our hosting provider’s routine backups are removed automatically as those backups expire.',
      'Analytics data: kept only as long as needed to understand how the site is used, and never linked to your health information.',
      'Messages: up to 2 years, unless needed longer for a legal reason.',
    ],
  },
  {
    heading: '7. Your rights',
    paragraphs: ['Under the DPDP Act you can:'],
    bullets: [
      'ask what personal data we hold about you and how we use it;',
      'correct, complete or update it;',
      'ask us to erase it, or close your account;',
      'withdraw your consent at any time (this doesn’t affect anything done before you withdrew);',
      'nominate someone to exercise your rights if you die or become unable to;',
      'raise a grievance with us.',
    ],
    after: 'Most of this you can do yourself in your personal space. For anything else, email us (section 9).',
  },
  {
    heading: '8. Security',
    paragraphs: [
      'We use encryption in transit, hashed passwords and restricted access to protect your data. No system is perfectly secure; if a breach affects you, we will tell you and the authorities as the law requires.',
    ],
  },
  {
    heading: '9. Grievance Officer and contact',
    paragraphs: [
      'Grievance Officer, Mediatine Adscale Private Limited',
      'Office no 202B, 2nd Floor, F-344/1, Tara Bhawan, Lado Sarai, New Delhi 110030, India',
      'Email: hello@overall-wellbeing.com (subject: "Privacy request")',
      'We aim to respond within 7 working days. If you are not satisfied with our response, you may complain to the Data Protection Board of India.',
    ],
  },
  {
    heading: '10. Changes to this policy',
    paragraphs: [
      'If we make important changes, we will update the date above and, where appropriate, tell you on the site or by email.',
    ],
  },
  {
    heading: '11. Not medical advice',
    paragraphs: [
      'OVERALL WELLBEING provides general wellbeing information only. See our Medical Disclaimer (/medical-disclaimer).',
    ],
  },
];

export default function Privacy() {
  usePageMeta({
    title: 'Privacy Policy | OVERALL WELLBEING',
    description:
      'How OVERALL WELLBEING collects, uses and protects your information — including your account, saved guides, habits, health metrics and medication records.',
    path: '/privacy',
    noindex: false,
  });

  return (
    <LegalLayout
      testId="privacy-page"
      eyebrow="Trust & Ethics"
      title="Privacy Policy"
      intro='OVERALL WELLBEING ("we", "us") is published by Mediatine Adscale Private Limited, Office no 202B, 2nd Floor, F-344/1, Tara Bhawan, Lado Sarai, New Delhi 110030, India. We are the data fiduciary responsible for your personal data under India’s Digital Personal Data Protection Act, 2023. This policy explains, in plain language, what we collect, why, and the choices you have.'
      updated="October 2026"
      sections={SECTIONS}
    />
  );
}
